import { DABBEH_API_URL } from './constants';
import { noteServerDate } from './functions/serverClock';
import type { DabbehDoctor, DabbehMembership } from './types';

/**
 * Client of «دبّه», the queue service (repo Mohammadi/dabbeh). Off while DABBEH_API_URL is empty: the
 * queue then lives on this device only, as the prototype always did. Every call carries the patient's
 * login cookie (`credentials: include`); the service verifies it like hesarak does.
 */
export const isQueueApi = () => !!DABBEH_API_URL;

export interface ServerOffer {
  id: number;
  request_code: string;
  visit_at: string | null;
  visit_label: string | null;
  hold_expires_at: string;
  center_id: string;
  service_id: string;
  server_id: string;
  user_center_id: string;
}

export interface ServerSeat {
  seat_id: number;
  slug: string;
  queue_id: number;
  position: number;
  status: 'waiting' | 'offered' | 'missed';
  tracking_code: string;
  joined_at: string;
  /** From the doctor's public profile, the same on every device (null fields when the profile was not read). */
  doctor?: { name: string | null; center_name: string | null } | null;
  center_id?: string;
  /** The doctor's daily release clock, «HH:MM» Tehran time (set per queue in the admin panel). */
  release_time?: string | null;
  offer: ServerOffer | null;
}

/**
 * «07:45» (Tehran) → that clock today, as an ISO time; `formatReleaseLine(…, { daily: true })` moves it on
 * to the next day once it has passed. Null for anything that is not a clock.
 */
export const releaseAtFrom = (releaseTime?: string | null, now = Date.now()): string | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(releaseTime ?? '').trim());
  if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) return null;
  // Tehran is UTC+03:30 all year (no daylight saving since 2022)
  const tehranDay = new Date(now + 210 * 60_000).toISOString().slice(0, 10);
  return new Date(`${tehranDay}T${match[1].padStart(2, '0')}:${match[2]}:00+03:30`).toISOString();
};

export class QueueApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message);
  }
}

/** No call waits longer than this: a hung service must not keep «ثبت نهایی نوبت» (or any screen) spinning. */
export const QUEUE_CALL_TIMEOUT_MS = 12_000;

const UNREACHABLE = 'ارتباط با صف برقرار نشد؛ دوباره تلاش کنید.';

const call = async <T>(path: string, init: Parameters<typeof fetch>[1] = {}): Promise<T> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), QUEUE_CALL_TIMEOUT_MS);
  const sentAt = Date.now();
  try {
    // trailing slash: the site has trailingSlash:true, which would answer every call with a redirect first
    const response = await fetch(`${DABBEH_API_URL}${path.endsWith('/') ? path : `${path}/`}`, {
      ...init,
      credentials: 'include',
      signal: controller.signal,
      headers: { ...(init?.body ? { 'content-type': 'application/json' } : {}), ...(init?.headers as Record<string, string>) },
    });
    // the service's clock, for the hold countdown (./functions/serverClock.ts)
    noteServerDate(response.headers?.get?.('date'), sentAt);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new QueueApiError(response.status, body?.code, body?.message || UNREACHABLE);
    return body as T;
  } catch (error) {
    if (controller.signal.aborted) throw new QueueApiError(0, 'timeout', UNREACHABLE);
    throw error;
  } finally {
    clearTimeout(timer);
  }
};

const slugPath = (slug: string) => encodeURIComponent(slug);

export const queueApi = {
  summary: (slug: string) => call<{ enabled: boolean; waiting?: number; hold_minutes?: number }>(`/v1/queues/${slugPath(slug)}`),
  mySeats: () => call<{ seats: ServerSeat[] }>('/v1/me/seats').then(body => body.seats),
  join: (slug: string) => call<{ seat: ServerSeat }>(`/v1/queues/${slugPath(slug)}/seats`, { method: 'POST' }).then(body => body.seat),
  leave: (slug: string) => call<{ left: boolean }>(`/v1/queues/${slugPath(slug)}/seats/me`, { method: 'DELETE' }),
  pass: (offerId: number) => call<{ seat: ServerSeat }>(`/v1/offers/${offerId}/pass`, { method: 'POST' }).then(body => body.seat),
  refresh: (offerId: number) => call<{ seat: ServerSeat }>(`/v1/offers/${offerId}/refresh`, { method: 'POST' }).then(body => body.seat),
  // keepalive: sent while the page already moves on to the receipt or the payment gateway
  booked: (offerId: number, bookId: string) =>
    call<{ booked: boolean }>(`/v1/offers/${offerId}/booked`, { method: 'POST', body: JSON.stringify({ book_id: bookId }), keepalive: true }),
};

/**
 * The service's seat in the store's shape. The doctor's name, office and release clock come from the service
 * (right on every device); what this device knows from the profile visit fills the rest (photo, expertise).
 */
export const toMembership = (seat: ServerSeat, doctor: DabbehDoctor, viaSubscription = false): DabbehMembership => ({
  doctor: {
    ...doctor,
    name: seat.doctor?.name || doctor.name,
    centerName: seat.doctor?.center_name || doctor.centerName,
    centerId: doctor.centerId ?? seat.center_id,
    releaseAt: releaseAtFrom(seat.release_time) ?? doctor.releaseAt ?? null,
  },
  joinedAt: seat.joined_at,
  position: seat.position,
  trackingCode: seat.tracking_code,
  status: seat.status,
  viaSubscription,
  seatId: seat.seat_id,
  offer: seat.offer
    ? {
        id: seat.offer.id,
        // no time from the clinic: the screens show its own wording (`visit_label`), never the hold's deadline
        visitAt: seat.offer.visit_at ?? undefined,
        visitLabel: seat.offer.visit_label ?? undefined,
        holdExpiresAt: seat.offer.hold_expires_at,
        centerId: seat.offer.center_id,
        serviceId: seat.offer.service_id,
        serverId: seat.offer.server_id,
        userCenterId: seat.offer.user_center_id,
        requestCode: seat.offer.request_code,
      }
    : undefined,
});
